/**
 * Employee Context Utilities
 * 
 * This utility provides functions to handle employee selection events
 * and integrate employee data with the field structure.
 */

import {
  getStoredFieldStructure,
  setCurrentContext,
  updateStructureForContext
} from './fieldStructureUtils';
import { CONTEXT_TYPES } from './fieldContextUtils';
import { isDocumentField } from './fieldPatternUtils';
import AppService from '../Configurations/Appservice';

/**
 * Resolves the customer organisation's address for autofill. Accepts
 * either a pre-loaded org object (with .address) or an org id string —
 * in the latter case it fetches /v1/organisation/details once and pulls
 * .address. Returns null when no address exists (callers should NOT
 * fall back to employee.address — address always comes from the org).
 *
 * Lightweight: hits /v1/organisation/details which returns a bare
 * TOrganisation; does NOT call the heavier TTicketOrgDetails endpoint.
 */
export const fetchOrganisationAddress = async (customerOrganisation) => {
  try {
    if (customerOrganisation && typeof customerOrganisation === 'object') {
      return customerOrganisation.address || null;
    }
    if (customerOrganisation && typeof customerOrganisation === 'string') {
      const resp = await AppService.get('v1/organisation/details', { params: { organisationId: customerOrganisation } });
      return (resp && (resp.address || resp.data?.address)) || null;
    }
  } catch (e) {
    console.warn('Failed to fetch organisation for address auto-fill:', e?.message || e);
  }
  return null;
};

/**
 * Formik-specific helper: fills the 'address' Formik field from the
 * customer organisation's address. Tolerates either form shape — a
 * top-level `values.address` (NewTicket / OnBehalfTicket) or an entry
 * inside `values.fields[]` (workflow-style). Leaves the field blank
 * when the org has no address (we do NOT fall back to employee.address).
 *
 * The `currentFields` arg is no longer used to gate the autofill; it's
 * kept for backward compat. setFieldValue on a key the form doesn't
 * have is a no-op, so unconditionally setting is safe.
 */
export const fillAddressFromCustomerOrg = async (currentFields, customerOrganisation, formRef, setPopulatedFields) => {
  const address = await fetchOrganisationAddress(customerOrganisation);
  if (address && formRef?.current) {
    console.log('Auto-filling address from organisation:', address);
    formRef.current.setFieldValue('address', address);
    if (setPopulatedFields) {
      setPopulatedFields(prev => new Set([...prev, 'address']));
    }
  } else {
    console.log('Organisation has no address; leaving address field blank');
  }
};

/**
 * Handles employee selection and updates field structure
 *
 * @param {Object} employee - Selected employee data
 * @param {Object} formRef - Reference to formik form
 * @param {Function} setFields - State setter for fields
 * @param {Function} setUid - State setter for UID
 * @param {Function} uidReady - Function to mark UID as ready
 * @param {Function} setPopulatedFields - State setter for populated fields
 * @param {Function} setSelectedEmployee - State setter for selected employee
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object|string} [customerOrganisation] - Customer organisation
 *        used to autofill the 'address' field. Either a pre-loaded org
 *        object (with .address) or the org id as a string (helper will
 *        fetch /v1/organisation/details). Falsy → no address autofill.
 * @returns {Object} - Updated field structure
 */
export const handleEmployeeSelection = (
  employee,
  formRef,
  setFields,
  setUid,
  uidReady,
  setPopulatedFields,
  setSelectedEmployee,
  dispatch,
  customerOrganisation = null
) => {
  console.log('Employee selected:', employee);

  // Set selected employee
  setSelectedEmployee(employee);

  // Switch to employee context
  setCurrentContext(CONTEXT_TYPES.EMPLOYEE_SELECTED);

  // Get stored field structure from service selection
  const storedStructure = getStoredFieldStructure();

  if (!storedStructure) {
    console.warn('No field structure found. Please select a service first.');
    return null;
  }

  // Update structure for employee context
  const updatedStructure = updateStructureForContext(storedStructure, CONTEXT_TYPES.EMPLOYEE_SELECTED);

  // Get current fields
  const currentFields = formRef.current?.values?.fields || [];

  // Auto-fill fields from employee data.
  // Note: 'address' is NOT in this list — for both employee and org
  // tickets we source the address from the customer organisation, not the
  // employee. See fillAddressFromCustomerOrg() below.
  if (currentFields && currentFields.length > 0) {
    const staticFieldsToFill = [
      'mobileNumber',
      'firstNameEn',
      'lastNameEn',
      'middleNameEn',
      'email',
      'nameEn'
    ];

    // Document fields can be auto-filled via handleDocumentFieldAutoFill

    // For each field in our form
    currentFields.forEach(field => {
      // Check if this field is one we want to auto-fill
      if (staticFieldsToFill.includes(field.name)) {
        // Check if the employee data has this field
        if (employee[field.name] !== undefined && employee[field.name] !== null) {
          console.log(`Auto-filling field ${field.name} with value:`, employee[field.name]);

          // Set the field value in the form
          formRef.current.setFieldValue(field.name, employee[field.name]);

          // Mark this field as populated
          setPopulatedFields(prev => new Set([...prev, field.name]));
        }
      }

      // Auto-fill document fields
      if (isDocumentField(field)) {
        handleDocumentFieldAutoFill(field.name, employee, field, formRef);
      }
    });
  }

  // Fill the 'address' field from the customer organisation regardless of
  // whether the form uses values.fields[] (workflow-style) or flat top-level
  // values (NewTicket). setFieldValue is a no-op on keys the form doesn't have.
  // Fire-and-forget — the caller doesn't need to await this.
  fillAddressFromCustomerOrg(currentFields, customerOrganisation, formRef, setPopulatedFields);
  
  // Set employee ID in form
  if (employee.id) {
    formRef.current.setFieldValue('employeeId', employee.id);
  }
  
  // Set customer UID for ticket creation
  if (employee.uid) {
    console.log('Setting customer UID from employee data:', employee.uid);
    
    // Set the UID in the form values
    formRef.current.setFieldValue('uid', employee.uid);
    
    // Explicitly store employee UID in form values for file uploads
    console.log('Setting employeeUid in form values for file uploads:', employee.uid);
    formRef.current.setFieldValue('employeeUid', employee.uid);
    formRef.current.setFieldValue('employee', employee);
    
    // Update the UID state
    setUid(employee.uid);
    
    // Mark UID as ready
    console.log('Marking UID as ready:', employee.uid);
    uidReady(true);
  }
  
  return updatedStructure;
};

/**
 * Auto-fills document fields from employee data
 * 
 * @param {String} fieldName - Name of the document field
 * @param {Object} employee - Employee data
 * @param {Object} field - Field object
 * @param {Object} formRef - Reference to formik form
 */
export const handleDocumentFieldAutoFill = (fieldName, employee, field, formRef) => {
  // Check if employee has documents
  if (!employee.documents || !Array.isArray(employee.documents)) {
    console.log(`No documents found for employee ${employee.uid}`);
    return;
  }
  
  // Map field names to document types
  const fieldToDocumentType = {
    'passportCopy': 'PASSPORT',
    'visaPage': 'VISA',
    'birthCertificate': 'BIRTH_CERTIFICATE',
    'salaryCertificate': 'SALARY_CERTIFICATE',
    'labourContract': 'LABOUR_CONTRACT',
    'emiratesId': 'EMIRATES_ID',
    'photo': 'PHOTO',
    'shinasnama': 'SHINASNAMA'
  };
  
  // Get document type for this field
  const documentType = fieldToDocumentType[fieldName];
  
  if (!documentType) {
    console.log(`No document type mapping found for field ${fieldName}`);
    return;
  }
  
  // Find matching document
  const matchingDocument = employee.documents.find(doc => doc.type === documentType);
  
  if (matchingDocument) {
    console.log(`Found matching document for ${fieldName}:`, matchingDocument);
    
    // Set document reference in form
    formRef.current.setFieldValue(`${fieldName}Reference`, matchingDocument.id);
    
    // Set document URL for preview
    if (matchingDocument.url) {
      formRef.current.setFieldValue(`${fieldName}Url`, matchingDocument.url);
    }
  } else {
    console.log(`No matching document found for ${fieldName} with type ${documentType}`);
  }
};
